import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import Swal from 'sweetalert2';
import Header from '../../Components/Header';
import Footer from '../../Components/Footer';
import Preloader from '../../Components/Preloader';
import Contact_Us_Seo from '../../SEO/Contact_Us_Seo';

// Yup schema validation
const schema = yup.object().shape({
  firstn: yup
    .string()
    .required('First name is required')
    .matches(/^[A-Za-z\s]+$/, 'Only alphabets are allowed'),
  email: yup.string().email('Invalid email').required('Email is required'),
  subject: yup.string().required('Subject is required'),
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(/^[0-9]{10,15}$/, 'Only numbers, 10-15 digits allowed'),
  message: yup.string().required('Message is required'),
  recaptcha: yup.string().required('Please complete the CAPTCHA'),
});

function Contact() {
  const [isLoading, setIsLoading] = useState(true);
  const recaptchaRef = useRef(null); // Ref for ReCAPTCHA
  const { register, handleSubmit, formState: { errors }, setValue, reset } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 500);

    const handleScroll = () => {
      const scroll = window.scrollY;
      const stickyHeader = document.getElementById('sticky-header');
      const scrollToTarget = document.querySelector('.scroll-to-target');

      if (scroll < 245) {
        stickyHeader?.classList.remove('sticky-menu');
        scrollToTarget?.classList.remove('open');
      } else {
        stickyHeader?.classList.add('sticky-menu');
        scrollToTarget?.classList.add('open');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const onSubmit = async (data) => {
    try {
      const response = await fetch('https://pay-quik.co.uk/prod/api/contactus/savecontactdetails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.firstn,
          email: data.email,
          phoneno: data.phone,
          subject: data.subject,
          comment: data.message,
          reCaptcha: data.recaptcha,
        })
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Form submitted successfully',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'OK'
        });
        reset(); // Clear form
        if (recaptchaRef.current) {
          recaptchaRef.current.reset(); // Clear CAPTCHA
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed!',
          text: 'Form submission failed. Please try again.',
          confirmButtonColor: '#d33',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'An error occurred. Please try again later.',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleRecaptchaChange = (value) => {
    setValue('recaptcha', value);
  };

  return (
    <div>
      {isLoading ? (
        <Preloader />
      ) : (
        <div>
          <Contact_Us_Seo />
          {/* back to top start */}
          <button
            className="scroll-top scroll-to-target"
            data-target="html"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <i className="fa-solid fa-angles-up" />
          </button>
          <div>
            <Header />
            <section
              id="site-breadcumb"
              style={{ backgroundImage: 'url("assets/images/breadcumb/breadcumb-bd.svg")' }}
            >
              <div className="site-breadcumb-wrapper">
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <div className="breadcumb-content">
                        <h1 className="breadcumb-title">Contact Us</h1>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="about-sec contact-page-sec sec-ptb">
              <div className="container">
                <div className="row">
                  {/* Contact Form */}
                  <div className="col-lg-6">
                    <div className="sec-content">
                      <h2 className="sec-title">Get A Quote</h2>
                      <form onSubmit={handleSubmit(onSubmit)} className="itco-cform">
                        <div className="row">
                          <div className="col-sm-12">
                            <div className="contact-field">
                              <input
                                type="text"
                                id="firstn"
                                placeholder="Enter Name"
                                {...register('firstn')}
                                onInput={(e) => {
                                  e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, '');
                                }}
                              />
                              <span className="error">{errors.firstn?.message}</span>
                            </div>
                          </div>
                          <div className="col-sm-6">
                            <div className="contact-field">
                              <input
                                type="email"
                                id="email"
                                placeholder="Enter Email"
                                {...register('email')}
                              />
                              <span className="error">{errors.email?.message}</span>
                            </div>
                          </div>
                          <div className="col-sm-6">
                            <div className="contact-field">
                              <input
                                type="text"
                                id="phone"
                                placeholder="Enter Phone"
                                {...register('phone')}
                                onInput={(e) => {
                                  e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                }}
                              />
                              <span className="error">{errors.phone?.message}</span>
                            </div>
                          </div>
                          <div className="col-sm-12">
                            <div className="contact-field">
                              <input
                                type="text"
                                id="subject"
                                placeholder="Enter Subject"
                                {...register('subject')}
                              />
                              <span className="error">{errors.subject?.message}</span>
                            </div>
                          </div>
                          <div className="col-sm-12">
                            <div className="contact-field">
                              <textarea
                                id="message"
                                cols={40}
                                rows={4}
                                placeholder="Enter Message..."
                                {...register('message')}
                              />
                              <span className="error">{errors.message?.message}</span>
                            </div>
                            <div className="contact-field">
                              <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey="6LcG1xUqAAAAAAnUyWAe30HcPO3Sih1mU4Df6AQO"
                                onChange={handleRecaptchaChange}
                              />
                              <span className="error">{errors.recaptcha?.message}</span>
                            </div>
                            <div className="itco-form-btn">
                              <button type="submit" className="thm-btn mt-3">
                                <span className="txt">Submit</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="col-lg-6 tab-col-gap">
                    <div className="sec-content">
                      <h2 className="sec-title">Get In Touch</h2>
                      <p>
                        These are the phrases we stay via way of means by in the whole lot we do.
                        Each brand we build, and we create.
                      </p>
                      <div className="get-in-touch">
                        <div className="qinfo-item">
                          <div className="qinfo-icon">
                            <i className="flaticon-maps-and-flags" />
                          </div>
                          <div className="qinfo-box">
                            <h5>Address</h5>
                            <NavLink
                              to="https://www.google.com/maps/search/?api=1&query=167-169+Great+Portland+Street,+5th+Floor,+London+W1W+5PF"
                              target="_blank"
                            >
                              167-169 Great Portland Street, 5th Floor, London W1W 5PF.
                            </NavLink>
                          </div>
                        </div>
                        <div className="qinfo-item">
                          <div className="qinfo-icon">
                            <i className="flaticon-call" />
                          </div>
                          <div className="qinfo-box">
                            <h5>Phone Number</h5>
                            <NavLink to="tel:447766431049">+44 7766 431 049</NavLink>
                          </div>
                        </div>
                        <div className="qinfo-item mb-0">
                          <div className="qinfo-icon">
                            <i className="flaticon-gmail-logo" />
                          </div>
                          <div className="qinfo-box">
                            <h5>Email ID</h5>
                            <NavLink to="mailto:info@pay-quik.com">info@pay-quik.com</NavLink>
                          </div>
                        </div>
                      </div>
                      <div className="company-social d-flex">
                        <NavLink to="https://www.facebook.com/PayQuikTransfer/" target="_blank">
                          <i className="fa-brands fa-facebook-f" />
                        </NavLink>
                        <NavLink to="https://www.youtube.com/channel/UComYbfKJFyGboOJg4lm8e3g" target="_blank">
                          <i className="fa-brands fa-youtube" />
                        </NavLink>
                        <NavLink to="https://x.com/PayQuikTransfer" target="_blank">
                          <i className="fa-brands fa-twitter" />
                        </NavLink>
                        <NavLink to="https://www.instagram.com/PayQuik.Transfer/" target="_blank">
                          <i className="fa-brands fa-instagram" />
                        </NavLink>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <Footer />
          </div>
        </div>
      )}
    </div>
  );
}

export default Contact;
